import { Injectable } from '@angular/core';
import { combineLatest, of } from 'rxjs';
import { concatMap, filter, map, switchMap, withLatestFrom, mergeMap, tap, take, catchError, mapTo } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import isEqual from 'lodash/isEqual';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';

import { ShiftService } from '../../services';
import { StartShiftPayload, StatusType } from '@models/*';
import {
    ReportShiftActionTypes,
    SelectReportDay,
    LoadReportShift, LoadReportShiftSuccess, LoadReportShiftFail,
    StartShiftBreak, StartShiftBreakSuccess, StartShiftBreakFail,
    EndShiftBreak, EndShiftBreakSuccess, EndShiftBreakFail,
    StartShift, StartShiftSuccess, StartShiftFail,
    EndShift, EndShiftSuccess, EndShiftFail,
    StartTourStopTracking, EndTourStopTrackingSuccess, EndTourStopTrackingFail,
    StartTourStopTrackingSuccess, StartTourStopTrackingFail,
    EndTourStopTracking,
    EditShift, EditShiftSuccess, EditShiftFail,
    EditShiftBreak, EditShiftBreakSuccess, EditShiftBreakFail,
    EditTourStopTracking, EditTourStopTrackingSuccess, EditTourStopTrackingFail,
    TourActionTypes,
    AddTrackedActions,
    AddRequiredTrackedActions,
    AddRequiredTrackedActionsFail
} from '../actions';
import {
    getCurrentStop, getRunningShift, getReportShiftsEntities, getCurrentShiftBreak, getCurrentTourStopTracking, getTours
} from '../selectors';
import { OverviewState } from '../registration';
import { getOfflineStatus, AppState } from '@appstore/*';
import { ensureShiftIsRunning } from '../utils';
import {assertAttributes} from "../../../shared/utilities/assert";
import { ComponentGeneratorService } from '../../../core/services/component-generator.service';
import { ConfirmationService } from '../../../core/services/confirmation.service';

@Injectable()
export class ReportShiftEffects {
    constructor(
        private actions$: Actions,
        private shiftService: ShiftService,
        private componentGenerator: ComponentGeneratorService,
        private confirmations: ConfirmationService,
        private store: Store<OverviewState | AppState>,
    ) {
    }

    selectReportDay$ = createEffect(() => this.actions$.pipe(
        ofType(ReportShiftActionTypes.SELECT_REPORT_DAY),
        map((action: SelectReportDay) => action.payload),
        withLatestFrom(
            this.store.select(getOfflineStatus),
            this.store.select(getReportShiftsEntities)
        ),
        filter(([date, isOffline, reportShifts]) => !isOffline && !reportShifts[date]),
        map(([date]) => new LoadReportShift(date)),
    ));

    loadReportShift$ = createEffect(() => this.actions$.pipe(
        ofType(ReportShiftActionTypes.LOAD_REPORT_SHIFT),
        map((action: LoadReportShift) => action.payload),
        switchMap(date => {
            return this.shiftService.getReportShift(date).pipe(
                map(res => {
                    if (res) {
                        return new LoadReportShiftSuccess({ date, reportShift: res });
                    } else {
                        return new LoadReportShiftFail({ date, error: null });
                    }
                })
            );
        }),
    ));

    startShift$ = createEffect(() => this.actions$.pipe(
        ofType(ReportShiftActionTypes.START_SHIFT),
        map((action: StartShift) => action.payload),
        mergeMap((payload: StartShiftPayload) => {
            return this.shiftService.startShift(payload).pipe(
                map(res => {
                    const shiftId = res?.id;
                    assertAttributes({shiftId});
                    return new StartShiftSuccess({ ...payload, shiftId });
                }),
                catchError(() => of(new StartShiftFail({ date: payload.date, error: null }))),
            );
        }),
    ));

    endShift$ = createEffect(() => this.actions$.pipe(
        ofType(ReportShiftActionTypes.END_SHIFT),
        map((action: EndShift) => action.payload),
        withLatestFrom(
            this.store.select(getCurrentShiftBreak),
            this.store.select(getCurrentTourStopTracking),
        ),
        tap(([payload, runningShiftBreak, runningTourStopTracking]) => {
            if (runningTourStopTracking) {
                const tourStopTrackingPayload = {
                    date: payload.date,
                    tourStopTrackingId: runningTourStopTracking.id,
                    previous: false,
                };
                this.store.dispatch(new EndTourStopTracking(tourStopTrackingPayload));
            }
            if (runningShiftBreak) {
                const breakPayload = {
                    date: payload.date,
                    shiftId: payload.shiftId,
                    breakId: runningShiftBreak.id,
                };
                this.store.dispatch(new EndShiftBreak(breakPayload));
            }
        }),
        switchMap(([payload]) => {
            return combineLatest([
                this.store.select(getCurrentShiftBreak),
                this.store.select(getCurrentTourStopTracking),
            ]).pipe(
                filter(([runningShiftBreak, runningTourStopTracking]) => !runningShiftBreak && !runningTourStopTracking),
                map(() => payload),
                take(1),
            );
        }),
        switchMap(actionPayload => {
            const { shiftId, ...httpPayload } = actionPayload;
            return this.shiftService.endShift(httpPayload).pipe(
                mapTo(new EndShiftSuccess(actionPayload)),
                catchError(() => of(new EndShiftFail({ date: actionPayload.date, error: null }))),
            );
        }),
    ));

    startShiftBreak$ = createEffect(() => this.actions$.pipe(
        ofType(ReportShiftActionTypes.START_SHIFT_BREAK),
        map((action: StartShiftBreak) => action.payload),
        mergeMap(payload => {
            return this.shiftService.sendShiftBreakUpdate({ date: payload.date }, 'start').pipe(
                map(res => {
                    const breakId = (res?.data && res.data.length) ? res.data[0].id : null;
                    assertAttributes({breakId});
                    return new StartShiftBreakSuccess({ ...payload, breakId });
                }),
                catchError(() => of(new StartShiftBreakFail({ date: payload.date, error: null }))),
            );
        }),
    ));

    endShiftBreak$ = createEffect(() => this.actions$.pipe(
        ofType(ReportShiftActionTypes.END_SHIFT_BREAK),
        map((action: EndShiftBreak) => action.payload),
        mergeMap(payload => {
            return this.shiftService.sendShiftBreakUpdate(payload, 'end').pipe(
                mapTo(new EndShiftBreakSuccess(payload)),
                catchError(() => of(new EndShiftBreakFail({ date: payload.date, error: null }))),
            );
        }),
    ));

    startTourStopTracking$ = createEffect(() => this.actions$.pipe(
        ofType(ReportShiftActionTypes.START_TOUR_STOP_TRACKING),
        map((action: StartTourStopTracking) => action.payload),
        ensureShiftIsRunning(this),
        withLatestFrom(
            this.store.select(getCurrentStop),
            this.store.select(getRunningShift),
            this.store.select(getCurrentTourStopTracking),
        ),
        filter(([payload, currentStop, runningShift, currentTourStopTracking]) => {
            // make sure tour stop tracking doesn't start twice, should be avoided by using the `ensureTourStopTrackingStarted` operator
            return !(currentTourStopTracking &&
                isEqual(currentTourStopTracking.locationGeo, currentStop.locationGeo) &&
                currentTourStopTracking.tourId === currentStop.tourId
            );
        }),
        concatMap(([actionPayload, currentStop, runningShift]) => {

            // When a driver starts a tour stop tracking before starting a shift,
            // a modal is shown to also start a shift. Because the start shift action
            // is done after executing the check-in, the timestamps that are calculated
            // for each action are also incorrect (check-in happens before shift start).
            // To prevent errors further downstream, clamp the start time of the check-in
            // to the start time of the shift.
            const shiftTimestamp = moment(runningShift.startedAt);
            const checkInTimestamp = moment(actionPayload.date);
            const correctedCheckInTimestamp = checkInTimestamp.isBefore(shiftTimestamp) ? shiftTimestamp : checkInTimestamp;

            const httpPayload = {
                date: correctedCheckInTimestamp.toISOString(true),
                locationGeo: actionPayload.tourStop.locationGeo,
                locationType: actionPayload.tourStop.locationType,
                address: actionPayload.tourStop.address,
                city: actionPayload.tourStop.city,
                sitename: actionPayload.tourStop.name,
                tourId: actionPayload.tourStop.tourId,
            };
            return this.shiftService.tourStopTrackingCheckIn(httpPayload).pipe(
                map(res => {
                    const tourStopTrackingId = res?.data?.id;
                    assertAttributes({tourStopTrackingId});
                    return new StartTourStopTrackingSuccess({
                        ...httpPayload,
                        shiftId: runningShift.id,
                        tour: actionPayload.tour,
                        tourStopTrackingId,
                    });
                }),
                catchError(() => of(new StartTourStopTrackingFail({ date: actionPayload.date, error: null }))),
            );
        }),
    ));

    endTourStopTracking$ = createEffect(() => this.actions$.pipe(
        ofType(ReportShiftActionTypes.END_TOUR_STOP_TRACKING),
        map((action: EndTourStopTracking) => action.payload),
        ensureShiftIsRunning(this),
        withLatestFrom(this.store.select(getCurrentTourStopTracking)),
        mergeMap(([{ previous, ...payload }, tourStopTracking]) => {
            if (!tourStopTracking) {
                return of(new EndTourStopTrackingFail({
                    date: payload.date,
                    error: 'END_TOUR_STOP_TRACKING_FAIL missing tourStopTracking'
                }));
            }

            const httpPayload = {
                date: payload.date,
                locationGeo: tourStopTracking.locationGeo,
                locationType: tourStopTracking.locationType,
                address: tourStopTracking.address,
                city: tourStopTracking.city,
                sitename: tourStopTracking.sitename,
                tourId: tourStopTracking.tourId,
                timestamp: moment().toISOString(true)
            };

            return this.shiftService.tourStopTrackingCheckOut(httpPayload, previous).pipe(
                map(() => {
                    return new EndTourStopTrackingSuccess({
                        ...httpPayload,
                        shiftId: tourStopTracking.shiftId,
                        tourStopTrackingId: tourStopTracking.id
                    });
                }),
                catchError(() => of(new EndTourStopTrackingFail({ date: payload.date, error: null }))),
            );
        }),
    ));

    editShift$ = createEffect(() => this.actions$.pipe(
        ofType(ReportShiftActionTypes.EDIT_SHIFT),
        map((action: EditShift) => action.payload),
        mergeMap(payload => {
            return this.shiftService.editShift(payload.editedData).pipe(
                map(res => {
                    if (res) {
                        return new EditShiftSuccess(payload);
                    } else {
                        return new EditShiftFail({ date: payload.date, error: null });
                    }
                }),
            );
        }),
    ));

    editShiftBreak$ = createEffect(() => this.actions$.pipe(
        ofType(ReportShiftActionTypes.EDIT_SHIFT_BREAK),
        map((action: EditShiftBreak) => action.payload),
        mergeMap(payload => {
            return this.shiftService.editShiftBreak(payload.editedData).pipe(
                map(res => {
                    if (res) {
                        return new EditShiftBreakSuccess(payload);
                    } else {
                        return new EditShiftBreakFail({ date: payload.date, error: null });
                    }
                }),
            );
        }),
    ));

    editTourStopTracking$ = createEffect(() => this.actions$.pipe(
        ofType(ReportShiftActionTypes.EDIT_TOUR_STOP_TRACKING),
        map((action: EditTourStopTracking) => action.payload),
        mergeMap(payload => {
            return this.shiftService.editTourStopTracking(payload.editedData).pipe(
                map(res => {
                    if (res) {
                        return new EditTourStopTrackingSuccess(payload);
                    } else {
                        return new EditTourStopTrackingFail({ date: payload.date, error: null });
                    }
                })
            );
        }),
    ));

    addTrackedAction$ = createEffect(() => this.actions$.pipe(
        ofType(TourActionTypes.ADD_REQUIRED_TRACKED_ACTIONS),
        map((action: AddRequiredTrackedActions) => action.payload),
        withLatestFrom(
            this.store.select(getCurrentTourStopTracking),
            this.store.select(getTours),
        ),
        map(([tsaUpdates, currentTST, tours]) => {
            const completedTSAIds = tsaUpdates
                .filter(tsaUpdate => [StatusType.COMPLETED, StatusType.COMPLETED_PLANNER].includes(tsaUpdate.status))
                .map(tsaUpdate => tsaUpdate.tourStopActionId);

            if (!currentTST) {
                return new AddRequiredTrackedActionsFail({
                    tourStopActionIds: completedTSAIds,
                    error: 'ADD_REQUIRED_TRACKED_ACTIONS_FAIL, no current tourStopTracking'
                });
            }

            const completedTSAsTourStop = tours.reduce((accum, item) => {
                return [
                    ...accum,
                    ...item.stops
                ];

            }, []).find(stop => {
                return stop.actions.find(action => completedTSAIds.includes(action.id));
            });

            const completedTSAsTour = tours.find(tour => tour.id === completedTSAsTourStop.tourId);

            const completedTSAsTrackedActions = completedTSAsTourStop.actions.filter(tsa => completedTSAIds.includes(tsa.id))
                .map(completedTSA => {
                    return {
                        id: `illegal-id-${uuidv4()}`, // TrackedAction IDs are generated in the back-end when completing actions, but are not passed to the FE in the response of those action-completing-calls (e.g., submitProtocol(), actionsStatusUpdate()). Generating a tagged TrackedAction ID here nevertheless, hoping it will not be used in back-end communication later on.
                        tourStopTrackingId: currentTST.id,
                        actionId: completedTSA.id,
                        machineInternalId: (completedTSA.transport && completedTSA.transport.machine && completedTSA.transport.machine.internalId) || '',
                        actionType: completedTSA.actionType,
                        fleetNumber: (completedTSA.transport && completedTSA.transport.machine && completedTSA.transport.machine.fleetNumber) || '',
                        cargoDescription: !!completedTSA.transport.cargoDescription ? completedTSA.transport.cargoDescription : '',
                        /* The TrackedAction attributes transportInternalId, completedAt and isUnfinish are not mapped => not required? */
                    };
                });

            const currentTSTTrackedActions = currentTST.trackedActions || [];

            const newTourStopTracking = {
                ...currentTST,
                tour: completedTSAsTour,
                trackedActions: [
                    ...currentTSTTrackedActions,
                    ...completedTSAsTrackedActions
                ]
            };

            return new AddTrackedActions(newTourStopTracking);
        }),
    ));
}
